/**
 * A entrada de UM campo personalizado, no controle certo para o tipo (#568).
 *
 * Quem usa guarda a mudança; este componente só traduz o que a pessoa digita
 * para o valor do contrato. Vazio é `null`: tira o valor do registro.
 */

import { useState, type ReactNode } from 'react';
import type { CampoPersonalizado, ValorDeCampo } from '@cliqcrm/contracts';
import { Campo, CampoArea, CampoSelecao } from '@/shared/ui';
import { emMicros } from '@/shared/lib/formato';
import './campos-personalizados.css';

function reaisDigitaveis(valor: ValorDeCampo | undefined): string {
  if (typeof valor !== 'object' || valor === null || Array.isArray(valor)) return '';
  return (valor.quantia_micros / 1_000_000).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function EntradaDoCampo({
  campo,
  valor,
  aoMudar,
  id,
}: {
  campo: CampoPersonalizado;
  valor: ValorDeCampo | undefined;
  aoMudar: (valor: ValorDeCampo | null) => void;
  /** Prefixo do id do controle, único na tela. */
  id: string;
}): ReactNode {
  const rotulo = campo.obrigatorio ? `${campo.nome} (obrigatório)` : campo.nome;
  // O valor em R$ é texto enquanto se digita: "1.5" no meio de "1.500" não é erro.
  const [reais, definirReais] = useState(() => reaisDigitaveis(valor));
  const controle = `${id}-${campo.id}`;

  switch (campo.tipo) {
    case 'texto_longo':
      return (
        <CampoArea
          id={controle}
          rotulo={rotulo}
          value={typeof valor === 'string' ? valor : ''}
          maxLength={5000}
          onChange={(e) => aoMudar(e.currentTarget.value === '' ? null : e.currentTarget.value)}
        />
      );
    case 'numero':
      return (
        <Campo
          id={controle}
          rotulo={rotulo}
          type="number"
          value={typeof valor === 'number' ? String(valor) : ''}
          onChange={(e) => {
            const texto = e.currentTarget.value;
            aoMudar(texto === '' || !Number.isFinite(Number(texto)) ? null : Number(texto));
          }}
        />
      );
    case 'dinheiro': {
      const micros = emMicros(reais);
      const invalido = reais.trim() !== '' && micros === null;
      return (
        <Campo
          id={controle}
          rotulo={rotulo}
          type="text"
          inputMode="decimal"
          placeholder="R$ 0,00"
          value={reais}
          aria-invalid={invalido}
          erro={invalido ? 'Escreva como você fala: 1.500,00 ou 1500.' : undefined}
          onChange={(e) => {
            const texto = e.currentTarget.value;
            definirReais(texto);
            const lido = emMicros(texto);
            if (texto.trim() === '') aoMudar(null);
            else if (lido !== null) aoMudar({ quantia_micros: lido, moeda: 'BRL' });
          }}
        />
      );
    }
    case 'data':
      return (
        <Campo
          id={controle}
          rotulo={rotulo}
          type="date"
          value={typeof valor === 'string' ? valor : ''}
          onChange={(e) => aoMudar(e.currentTarget.value === '' ? null : e.currentTarget.value)}
        />
      );
    case 'sim_nao':
      return (
        <CampoSelecao
          id={controle}
          rotulo={rotulo}
          value={valor === true ? 'sim' : valor === false ? 'nao' : ''}
          onChange={(e) => {
            const escolha = e.currentTarget.value;
            aoMudar(escolha === '' ? null : escolha === 'sim');
          }}
        >
          <option value="">Não informado</option>
          <option value="sim">Sim</option>
          <option value="nao">Não</option>
        </CampoSelecao>
      );
    case 'lista_unica': {
      const escolhida = typeof valor === 'string' ? valor : '';
      // A arquivada só aparece para quem já a tinha: escolha nova não a oferece.
      const opcoes = campo.opcoes.filter((opcao) => !opcao.arquivada || opcao.id === escolhida);
      return (
        <CampoSelecao
          id={controle}
          rotulo={rotulo}
          value={escolhida}
          onChange={(e) => aoMudar(e.currentTarget.value === '' ? null : e.currentTarget.value)}
        >
          <option value="">Não informado</option>
          {opcoes.map((opcao) => (
            <option key={opcao.id} value={opcao.id}>
              {opcao.arquivada ? `${opcao.nome} (arquivada)` : opcao.nome}
            </option>
          ))}
        </CampoSelecao>
      );
    }
    case 'lista_multipla': {
      const marcadas = Array.isArray(valor) ? valor : [];
      const opcoes = campo.opcoes.filter(
        (opcao) => !opcao.arquivada || marcadas.includes(opcao.id),
      );
      return (
        <fieldset className="campos-reg__varias">
          <legend className="campos-reg__legenda">{rotulo}</legend>
          {opcoes.map((opcao) => {
            const marcada = marcadas.includes(opcao.id);
            return (
              <label key={opcao.id} className="campos-reg__opcao">
                <input
                  type="checkbox"
                  checked={marcada}
                  onChange={() => {
                    const novas = marcada
                      ? marcadas.filter((idDaOpcao) => idDaOpcao !== opcao.id)
                      : [...marcadas, opcao.id];
                    aoMudar(novas.length === 0 ? null : novas);
                  }}
                />
                {opcao.arquivada ? `${opcao.nome} (arquivada)` : opcao.nome}
              </label>
            );
          })}
        </fieldset>
      );
    }
    default:
      return (
        <Campo
          id={controle}
          rotulo={rotulo}
          type="text"
          value={typeof valor === 'string' ? valor : ''}
          maxLength={200}
          onChange={(e) => aoMudar(e.currentTarget.value === '' ? null : e.currentTarget.value)}
        />
      );
  }
}
